import { useState, useEffect, useCallback, useRef } from 'react';

interface FilterConfig {
  [key: string]: string;
}

export const useGridFilters = (
  initialFilters: FilterConfig,
  onFilterChange: (filterString: string) => void
) => {
  const [filters, setFilters] = useState<FilterConfig>(initialFilters);

  // Use ref to store the latest callback without causing re-renders
  const onFilterChangeRef = useRef(onFilterChange);

  // Update ref when callback changes
  onFilterChangeRef.current = onFilterChange;

  // Build filter string whenever filters change
  useEffect(() => {
    let tempDataUrlQueryFilter = '';
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        tempDataUrlQueryFilter += `&${key}=${value}`;
      }
    });
    onFilterChangeRef.current(tempDataUrlQueryFilter);
  }, [filters]); // Only depend on filters

  // Update a specific filter (for text inputs - no auto-apply)
  const updateFilter = useCallback((key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Update a specific filter and auto-apply (for dropdowns)
  const updateFilterAndApply = useCallback(
    (key: string, value: string, refreshCallback: () => void) => {
      setFilters((prev) => {
        const newFilters = { ...prev, [key]: value };

        // Build the filter string immediately with the new filters
        let tempDataUrlQueryFilter = '';
        Object.entries(newFilters).forEach(([filterKey, filterValue]) => {
          if (filterValue) {
            tempDataUrlQueryFilter += `&${filterKey}=${filterValue}`;
          }
        });

        // Update the query filter immediately
        onFilterChangeRef.current(tempDataUrlQueryFilter);

        // Use setTimeout to ensure the queryFilter state propagates before refresh
        setTimeout(() => {
          refreshCallback();
        }, 0);

        return newFilters;
      });
    },
    []
  );

  // Clear all filters back to initial state
  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Set multiple filters at once
  const setMultipleFilters = useCallback((newFilters: FilterConfig) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Function to trigger refresh with current filters
  const applyFilters = useCallback((refreshCallback: () => void) => {
    refreshCallback();
  }, []);

  return {
    filters,
    updateFilter,
    updateFilterAndApply,
    clearFilters,
    setMultipleFilters,
    applyFilters,
  };
};
